import React from 'react';
import { Table, Badge, SortableTh, RowActions, ACTIONS_COL_WIDTH } from '@/components/ui';
import { Asset, AssetStatus } from '../../../types/Asset';
import { ASSET_TYPE_LABELS, ASSET_TYPES } from '../../../constants/assetTypes';

interface AssetsTableProps {
  assets: Asset[];
  onRowClick: (asset: Asset) => void;
  onDelete?: (assetId: number) => void;
  onToggleStatus?: (assetId: number) => void;
  onShowHistory?: (asset: Asset) => void;
  sortBy?: string | null;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string, dir: 'asc' | 'desc') => void;
}

const AssetsTable: React.FC<AssetsTableProps> = ({ assets, onRowClick, onDelete, onToggleStatus, onShowHistory, sortBy = null, sortDir = 'asc', onSort }) => {
  // RowActions stops row-click propagation for the whole cell, so these no longer need wrappers.

  const getAssetTypeLabel = (assetType: string): string => {
    // Map AssetType values to ASSET_TYPES values
    const typeMapping: Record<string, ASSET_TYPES> = {
      'road': ASSET_TYPES.ROAD,
      'culvert': ASSET_TYPES.CULVERT,
      'sign': ASSET_TYPES.SIGN,
      'cattleguard': ASSET_TYPES.CATTLEGUARD, 
      'restarea': ASSET_TYPES.RESTAREA, 
      'structure': ASSET_TYPES.STRUCTURE 
    };

    const mappedType = typeMapping[assetType];
    if (mappedType && ASSET_TYPE_LABELS[mappedType]) {
      return ASSET_TYPE_LABELS[mappedType];
    }
    
    // Fallback to the original asset type if no mapping found
    return assetType;
  };

  return (
    <div className="tw-w-full tw-overflow-x-auto">
      <Table hover>
        <thead>
          <tr>
            <SortableTh label="Asset Number" sortKey="assetNumber" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Name" sortKey="name" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Type" sortKey="assetType" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Status" sortKey="status" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Subtype" sortKey="subtype" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Description" sortKey="description" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <th>Service Area</th>
            <SortableTh label="Contract Area" sortKey="contractArea" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Road Length (km)" sortKey="length" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Winter Class" sortKey="winterClass" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            <SortableTh label="Summer Class" sortKey="summerClass" activeKey={sortBy} direction={sortDir} onSort={onSort} />
            {(onDelete || onToggleStatus || onShowHistory) && (
              <th style={{ width: ACTIONS_COL_WIDTH }} className="tw-text-right">Actions</th>
            )}
          </tr>
        </thead>
        <tbody>
          {assets.map((asset) => (
            <tr key={asset.id} onClick={() => onRowClick(asset)} style={{ cursor: 'pointer' }}>
              <td>{asset.assetNumber}</td>
              <td>{asset.name}</td>
              <td>
                <Badge bg="info" text="dark">
                  {getAssetTypeLabel(asset.assetType)}
                </Badge>
              </td>
              <td>
                <Badge bg={asset.status === AssetStatus.Disabled ? 'secondary' : 'success'}>
                  {asset.status || AssetStatus.Active}
                </Badge>
              </td>
              <td>
                {asset.subtype ? (
                  <Badge bg="secondary">{asset.subtype}</Badge>
                ) : '-'}
              </td>
              <td>{asset.description || '-'}</td>
              <td>
                {asset.contractArea?.foremanAreas && asset.contractArea.foremanAreas.length > 0 ? (
                  <div className="tw-flex tw-flex-wrap tw-gap-1">
                    {Array.from(new Set(asset.contractArea.foremanAreas.map(fa => fa.serviceArea?.name).filter(Boolean))).map((serviceAreaName, index) => (
                      <Badge key={index} bg="secondary" className="tw-mr-1">
                        {serviceAreaName}
                      </Badge>
                    ))}
                  </div>
                ) : '-'}
              </td>
              <td>
                {asset.contractArea?.name ? (
                  <Badge bg="primary">{asset.contractArea.name}</Badge>
                ) : '-'}
              </td>
              <td>{asset.assetType === 'road' ? (asset.length ? `${asset.length.toFixed(2)} km` : '-') : '-'}</td>
              <td>{asset.assetType === 'road' ? (asset.winterClass || '-') : '-'}</td>
              <td>{asset.assetType === 'road' ? (asset.summerClass || '-') : '-'}</td>
              {(onDelete || onToggleStatus || onShowHistory) && (
                <td>
                  <RowActions
                    actions={[
                      {
                        kind: 'history',
                        label: 'Asset History',
                        onClick: () => onShowHistory?.(asset),
                        hidden: !onShowHistory,
                      },
                      asset.status === AssetStatus.Disabled
                        ? {
                            kind: 'enable',
                            label: 'Enable Asset',
                            onClick: () => onToggleStatus?.(asset.id),
                            hidden: !onToggleStatus,
                          }
                        : {
                            kind: 'disable',
                            label: 'Disable Asset',
                            onClick: () => onToggleStatus?.(asset.id),
                            hidden: !onToggleStatus,
                          },
                      {
                        kind: 'delete',
                        label: 'Delete Asset',
                        onClick: () => onDelete?.(asset.id),
                        hidden: !onDelete,
                      },
                    ]}
                  />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
      {assets.length === 0 && (
        <div className="tw-text-center tw-p-4 tw-text-content-muted">
          No assets found
        </div>
      )}
    </div>
  );
};

export default AssetsTable;